import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError } from './client';
import { createProviderAccount, createScmConnection, getBitriseSetup, listBitriseApps, listScmConnections, listScmRepositories, listProviderAccounts, testProviderAccount, testScmConnection, provisionBitrise, syncBitriseWorkflows, updateScmConnection, updateProviderAccount, type BitriseProvisionInput, type ProviderAccountCreateInput, type ProviderAccountPatchInput } from './endpoints';
import { qk } from './queryKeys';

/* ---------- Provider accounts ---------- */
export const useProviderAccounts = (enabled = true) =>
  useQuery({ queryKey: qk.providerAccounts, queryFn: async () => (await listProviderAccounts()).data, enabled });

/** A Bitrise workspace's apps; 404/501 (route not served yet) are final, the field then falls back to a slug input. */
export const useBitriseApps = (accountId: string | undefined, enabled = true) =>
  useQuery({
    queryKey: ['bitrise-apps', accountId ?? ''] as const,
    queryFn: () => listBitriseApps(accountId ?? ''),
    enabled: Boolean(accountId) && enabled,
    staleTime: 60_000,
    retry: (count, err) => count < 1 && !(err instanceof ApiError && (err.status < 500 || err.status === 501)),
  });

export function useProviderAccountMutations() {
  const qc = useQueryClient();
  const done = () => void qc.invalidateQueries({ queryKey: qk.providerAccounts });
  const create = useMutation({ mutationFn: (v: { body: ProviderAccountCreateInput; idempotencyKey: string }) => createProviderAccount(v.body, v.idempotencyKey), onSuccess: done });
  const patch = useMutation({ mutationFn: (v: { id: string; body: ProviderAccountPatchInput; etag?: string }) => updateProviderAccount(v.id, v.body, v.etag), onSuccess: done });
  const test = useMutation({ mutationFn: (v: { id: string; apply?: boolean }) => testProviderAccount(v.id, v.apply), onSuccess: done });
  return { create, patch, test };
}

/* ---------- Source control and "Create on Bitrise" ---------- */
/** An API without source control answers 404/501: the feature is then simply absent. */
export const scmUnsupported = (err: unknown): boolean => err instanceof ApiError && (err.status === 404 || err.status === 501);


export const useScmConnections = (enabled = true) =>
  useQuery({
    queryKey: qk.scmConnections,
    queryFn: async () => (await listScmConnections()).data,
    enabled,
    retry: (count, err) => count < 1 && !(err instanceof ApiError && (err.status < 500 || err.status === 501)),
  });

export const useScmRepositories = (connectionId: string | undefined, query: string, enabled = true) =>
  useQuery({
    queryKey: qk.scmRepositories(connectionId ?? '', query),
    queryFn: () => listScmRepositories(connectionId ?? '', query),
    enabled: Boolean(connectionId) && enabled,
    staleTime: 30_000,
    retry: (count, err) => count < 1 && !(err instanceof ApiError && err.status < 500),
  });

export function useScmConnectionMutations() {
  const qc = useQueryClient();
  const done = () => void qc.invalidateQueries({ queryKey: qk.scmConnections });
  const create = useMutation({ mutationFn: (v: { body: { name: string; workspace: string; credentialId: string }; idempotencyKey?: string }) => createScmConnection(v.body, v.idempotencyKey), onSuccess: done });
  const patch = useMutation({ mutationFn: (v: { id: string; body: { name?: string; credentialId?: string }; etag?: string }) => updateScmConnection(v.id, v.body, v.etag), onSuccess: done });
  const test = useMutation({ mutationFn: (v: { id: string }) => testScmConnection(v.id), onSuccess: done });
  return { create, patch, test };
}

export const useBitriseSetup = (appId: string | undefined, enabled = true) =>
  useQuery({
    queryKey: qk.bitriseSetup(appId ?? ''),
    queryFn: () => getBitriseSetup(appId ?? ''),
    enabled: Boolean(appId) && enabled,
    retry: (count, err) => count < 1 && !(err instanceof ApiError && err.status < 500),
  });

export function useBitriseSetupMutations(appId: string) {
  const qc = useQueryClient();
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: qk.bitriseSetup(appId) });
    void qc.invalidateQueries({ queryKey: ['apps'] });
  };
  const provision = useMutation({ mutationFn: (body: BitriseProvisionInput) => provisionBitrise(appId, body), onSuccess: refresh, onError: refresh });
  const sync = useMutation({ mutationFn: () => syncBitriseWorkflows(appId), onSuccess: refresh, onError: refresh });
  return { provision, sync };
}
